import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders() {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtUGX(n: number) {
  return 'UGX ' + Math.round(n).toLocaleString('en-UG');
}

interface InventoryItem {
  id: string;
  name: string;
  sku?: string;
  unit: string;
  currentStock: number;
  reorderPoint: number;
  reorderQty: number;
  unitCost: number;
  supplier?: { name: string };
}

interface Supplier {
  id: string;
  name: string;
  contactName?: string;
  phone?: string;
  email?: string;
}

interface PurchaseOrder {
  id: string;
  orderNumber: string;
  status: string;
  supplier?: { name: string };
  totalAmount: number;
  createdAt: string;
}

interface StockMovement {
  id: string;
  type: string;
  quantity: number;
  note?: string;
  createdAt: string;
}

function Skeleton({ h = 48 }: { h?: number }) {
  return (
    <div style={{
      height: h, borderRadius: 8,
      background: 'var(--surface2)', animation: 'pulse 1.5s infinite',
    }} />
  );
}

function movBadgeClass(t: string) {
  if (t === 'IN' || t === 'PURCHASE') return 'badge-green';
  if (t === 'OUT' || t === 'WASTE') return 'badge-red';
  return 'badge-amber';
}

function poBadgeClass(s: string) {
  if (s === 'PENDING') return 'badge-amber';
  if (s === 'APPROVED') return 'badge-blue';
  if (s === 'RECEIVED') return 'badge-green';
  return 'badge-gray';
}

function MovementModal({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState('ADJUSTMENT');
  const [qty, setQty] = useState(0);
  const [note, setNote] = useState('');

  const { data: movements = [], isLoading } = useQuery<StockMovement[]>({
    queryKey: ['movements', item.id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/inventory/items/${item.id}/movements`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const record = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/v1/inventory/movements`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ inventoryItemId: item.id, type, quantity: qty, note }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movements', item.id] });
      qc.invalidateQueries({ queryKey: ['inventory-items'] });
      setQty(0); setNote('');
    },
  });

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{item.name}</h2>
            <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 2 }}>
              Stock: <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{item.currentStock} {item.unit}</span>
            </p>
          </div>
          <button className="modal-close" onClick={onClose} disabled={record.isPending}>&times;</button>
        </div>

        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Record Movement</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <select
              className="input"
              style={{ flex: 1 }}
              value={type}
              onChange={e => setType(e.target.value)}
            >
              <option value="IN">Stock In</option>
              <option value="OUT">Stock Out</option>
              <option value="ADJUSTMENT">Adjustment</option>
              <option value="WASTE">Waste</option>
              <option value="TRANSFER">Transfer</option>
            </select>
            <input
              className="input"
              style={{ width: 100 }}
              type="number"
              value={qty}
              onChange={e => setQty(parseFloat(e.target.value) || 0)}
              placeholder="Qty"
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              className="input"
              style={{ flex: 1 }}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Note (optional)"
            />
            <button
              className="btn btn-primary"
              onClick={() => { if (qty !== 0) record.mutate(); }}
              disabled={qty === 0 || record.isPending}
            >
              {record.isPending ? '...' : 'Record'}
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Recent Movements</p>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={40} />)}
            </div>
          ) : movements.length === 0 ? (
            <p style={{ color: 'var(--text-3)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No movements recorded.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {movements.map((m, i) => (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 0', borderBottom: i < movements.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`badge ${movBadgeClass(m.type)}`}>{m.type}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{m.note ?? '—'}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{m.quantity > 0 ? '+' : ''}{m.quantity} {item.unit}</p>
                    <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{new Date(m.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function InventoryPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'stock' | 'suppliers' | 'orders'>('stock');
  const [movementItem, setMovementItem] = useState<InventoryItem | null>(null);
  const [lowStockOnly, setLowStockOnly] = useState(false);

  const { data: items = [], isLoading: itemsLoading } = useQuery<InventoryItem[]>({
    queryKey: ['inventory-items', lowStockOnly],
    queryFn: async () => {
      const url = lowStockOnly
        ? `${API_BASE}/v1/inventory/items/low-stock`
        : `${API_BASE}/v1/inventory/items`;
      const res = await fetch(url, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data ?? [];
    },
  });

  const { data: suppliers = [], isLoading: suppliersLoading } = useQuery<Supplier[]>({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/inventory/suppliers`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const { data: orders = [], isLoading: ordersLoading } = useQuery<PurchaseOrder[]>({
    queryKey: ['purchase-orders'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/inventory/purchase-orders`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const { data: valuation } = useQuery<{ totalValue: number }>({
    queryKey: ['stock-valuation'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/inventory/reports/valuation`, { headers: authHeaders() });
      const j = await res.json();
      return j.data;
    },
  });

  const approvePO = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/inventory/purchase-orders/${id}/approve`, { method: 'POST', headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });

  const lowStockItems = items.filter(i => i.currentStock <= i.reorderPoint);

  return (
    <div>
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {items.length} items · {lowStockItems.length} low stock
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="stat-card">
          <p className="stat-label">Stock Value</p>
          <p className="stat-value" style={{ fontFamily: 'var(--font-mono)' }}>{valuation ? fmtUGX(valuation.totalValue) : '—'}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Total Items</p>
          <p className="stat-value">{items.length}</p>
        </div>
        <div className={`stat-card${lowStockItems.length > 0 ? ' red' : ''}`}>
          <p className="stat-label">Low Stock Alerts</p>
          <p className="stat-value">{lowStockItems.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {(['stock', 'suppliers', 'orders'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
              background: activeTab === tab ? 'var(--surface)' : 'transparent',
              color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
              border: 'none', cursor: 'pointer',
              boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {tab === 'stock' ? 'Stock' : tab === 'suppliers' ? 'Suppliers' : 'Purchase Orders'}
          </button>
        ))}
      </div>

      {activeTab === 'stock' && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-2)', marginBottom: 16, width: 'fit-content' }}>
            <input type="checkbox" checked={lowStockOnly} onChange={e => setLowStockOnly(e.target.checked)} />
            Low stock only
          </label>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {itemsLoading ? (
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} />)}
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No inventory items yet.</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style={{ textAlign: 'right' }}>Stock</th>
                      <th style={{ textAlign: 'right' }}>Reorder At</th>
                      <th style={{ textAlign: 'right' }}>Unit Cost</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => {
                      const isLow = item.currentStock <= item.reorderPoint;
                      return (
                        <tr key={item.id} style={isLow ? { background: 'var(--red-bg)' } : {}}>
                          <td>
                            <p style={{ fontWeight: 500, color: 'var(--text)' }}>{item.name}</p>
                            {item.sku && <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{item.sku}</p>}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{item.currentStock} {item.unit}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{item.reorderPoint} {item.unit}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(item.unitCost)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`badge ${isLow ? 'badge-red' : 'badge-green'}`}>{isLow ? 'Low Stock' : 'OK'}</span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setMovementItem(item)}>Movement</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'suppliers' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {suppliersLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} />)}
            </div>
          ) : suppliers.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No suppliers added yet.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text)' }}>{s.name}</td>
                      <td style={{ color: 'var(--text-2)' }}>{s.contactName ?? '—'}</td>
                      <td style={{ color: 'var(--text-2)' }}>{s.phone ?? '—'}</td>
                      <td style={{ color: 'var(--text-2)' }}>{s.email ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {ordersLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} />)}
            </div>
          ) : orders.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No purchase orders yet.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Supplier</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th>Date</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {orders.map(po => (
                    <tr key={po.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text)' }}>{po.orderNumber}</td>
                      <td style={{ color: 'var(--text-2)' }}>{po.supplier?.name ?? '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(po.totalAmount)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${poBadgeClass(po.status)}`}>{po.status}</span>
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>{new Date(po.createdAt).toLocaleDateString()}</td>
                      <td style={{ textAlign: 'right' }}>
                        {po.status === 'PENDING' && (
                          <button className="btn btn-ghost btn-sm" onClick={() => approvePO.mutate(po.id)}>Approve</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {movementItem && <MovementModal item={movementItem} onClose={() => setMovementItem(null)} />}
    </div>
  );
}
